import { useQuery } from '@tanstack/react-query';
import React, { FC } from 'react';
import styled from 'styled-components';

import { Dropdown, Icon, ObjectCard, Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { Loading, PageHeader } from '~/components';
import { ButtonGroupDropdown } from '~/components/ButtonGroupDropdown';
import { useDialog } from '~/components/Dialog/Provider';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { EntityRepository, EntityWithDetails } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { ApiError, EntityEntity } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';
import { formatString } from '~/util';

const StyledDropdown = styled(Dropdown)`
  margin: -10px 0;
`;

const RootEntityCardWrapper = styled(Inner)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  margin-top: 2px;
  justify-content: space-between;
`;

const RootEntityLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};
`;

const RootEntityCard = styled.div`
  flex-grow: 1;
`;

const getEntityName = (entity?: EntityWithDetails) => {
  if (!entity) return undefined;
  if (entity.personDetails)
    return `${entity.personDetails.firstName || ''} ${entity.personDetails.lastName || ''}`.trim();
  if (entity.businessDetails) return entity.businessDetails.businessName;
  return undefined;
};

const getEntityIcon = (entity?: EntityWithDetails): keyof typeof Icon | undefined => {
  if (!entity) return undefined;
  return entity.type === 'PERSON' ? 'User' : 'Business';
};

const columns: TableColumn<EntityEntity>[] = [
  {
    header: 'Name',
    accessor: 'name',
    width: { min: 200, max: '4fr' },
    cell: ({ value }) => <Table.TextCell value={value ?? ''} weight="medium" />,
  },
  {
    header: 'Type',
    accessor: 'type',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => (
      <Table.ChipCell
        value={formatString(value ?? '')}
        type="info"
        icon={value === 'BUSINESS' ? <Icon.Business /> : <Icon.User />}
      />
    ),
  },
  {
    header: 'Status',
    accessor: 'verificationStatus',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => {
      if (value === 'VERIFICATION_STATUS_NONE') {
        return <Table.ChipCell value="None" />;
      }
      if (value === 'UNVERIFIED') {
        return <Table.ChipCell value="Unverified" type="warning" icon={<Icon.CircleQuestionmark />} />;
      }
      if (value === 'PENDING') {
        return <Table.ChipCell value="Pending" icon={<Icon.Clock />} />;
      }
      if (value === 'MANUAL_REVIEW') {
        return <Table.ChipCell value="Manual review" icon={<Icon.User />} />;
      }
      if (value === 'VERIFIED') {
        return <Table.ChipCell value="Verified" type="success" icon={<Icon.CircleCheck />} />;
      }
      if (value === 'DENIED') {
        return <Table.ChipCell value="Denied" type="danger" icon={<Icon.CircleCross />} />;
      }
      return <Table.TextCell value="" />;
    },
  },
];

export const PageEntities: FC = () => {
  const { currentUser, currentPlatform, isSandbox } = useSessionStore();
  const { openAlert } = useDialog();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const mode: 'sandbox' | 'live' = isSandbox ? 'sandbox' : 'live';
  const { cursor, onNext, setPageSize, ...pagination } = usePaginationState();

  const {
    data: rootEntity,
    isLoading: isRootEntityLoading,
    error: rootEntityError,
  } = useQuery<EntityWithDetails | null>({
    queryKey: [currentPlatform?.id, 'platform', 'root-entity', isSandbox],
    queryFn: () => EntityRepository.getRoot(currentPlatform?.id ?? '', mode),
    enabled: !!currentPlatform?.id,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount: number, error: unknown) => {
      const apiError = error as Partial<ApiError> | undefined;
      if (apiError?.code === ApiErrCode.ResourceNotFound.toString()) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const {
    data: entitiesData,
    isLoading: isEntitiesLoading,
    refetch: refetchEntities,
  } = useQuery({
    queryKey: ['entities', 'list', currentPlatform?.id, isSandbox, cursor, pagination.pageSize],
    queryFn: async () =>
      EntityRepository.getAll({
        mode: isSandbox ? 'sandbox' : 'live',
        isRoot: false,
        limit: pagination.pageSize,
        startingAfter: cursor,
      }),
    enabled: !!currentPlatform?.id,
    gcTime: 0,
    staleTime: 0,
  });

  const handleDelete = (entry: any) => {
    openAlert({
      headline: 'Delete entity',
      content: 'This action cannot be undone.',
      confirmButton: {
        label: 'Delete',
        variant: 'danger',
      },
      onSuccess: () => {
        if (!currentUser) {
          return;
        }
        EntityRepository.delete(entry.id)
          .then(() => {
            addSuccessNotification({
              content: `${entry.name} deleted`,
            });
            refetchEntities();
          })
          .catch((error) => {
            addDangerNotification({
              content: error.message,
            });
          });
      },
    });
  };

  const entityName = getEntityName(rootEntity ?? undefined);
  const entityIcon = getEntityIcon(rootEntity ?? undefined);

  return (
    <>
      <PageHeader text="Entities" border={false}>
        <ButtonGroupDropdown
          buttons={[
            {
              label: 'Create Entity',
              icon: <Icon.Plus />,
              onClick: () => navigate(`${ROUTE.ENTITIES}/edit/person`),
            },
          ]}
          options={[
            {
              label: 'Person',
              icon: <Icon.User />,
              onClick: () => navigate(`${ROUTE.ENTITIES}/edit/person`),
            },
            {
              label: 'Business',
              icon: <Icon.Business />,
              onClick: () => navigate(`${ROUTE.ENTITIES}/edit/business`),
            },
          ]}
        />
      </PageHeader>

      <Loading isLoading={isRootEntityLoading}>
        {rootEntity && (
          <RootEntityCardWrapper px="var(--page-margin)" py={0}>
            <RootEntityLabel>Root Entity</RootEntityLabel>
            <RootEntityCard>
              <ObjectCard
                error={rootEntityError ? 'Failed to load root entity' : undefined}
                icon={entityIcon}
                isLoading={isRootEntityLoading}
                primaryText={entityName || 'Unnamed Entity'}
                id={{ label: 'ID:', value: rootEntity.id }}
                onClick={() =>
                  navigate(isSandbox ? ROUTE.ENTITIES_ROOT_ENTITY_SANDBOX : ROUTE.ENTITIES_ROOT_ENTITY_LIVE)
                }
              />
            </RootEntityCard>
          </RootEntityCardWrapper>
        )}

        <Inner pt={0} px="var(--page-margin)">
          <Table<EntityEntity>
            columns={columns}
            data={entitiesData?.entities ?? []}
            pagination={{
              hasNext: entitiesData?.hasMore ?? false,
              onNext: () =>
                entitiesData && entitiesData.entities && entitiesData.entities.length > 0
                  ? onNext({ cursor: entitiesData.entities[entitiesData.entities.length - 1].id ?? '' })
                  : undefined,
              onPageSizeChange: setPageSize,
              ...pagination,
            }}
            isLoading={isEntitiesLoading}
            rowAction={(row) => (
              <StyledDropdown
                isInside
                positionRight
                options={[
                  {
                    label: 'Edit',
                    onClick: (event: React.SyntheticEvent) => {
                      event.stopPropagation();
                      navigate(`${ROUTE.ENTITIES}/edit/${row.type?.toLowerCase() ?? 'person'}/${row.id}`);
                    },
                  },
                  {
                    label: 'Delete',
                    isDanger: true,
                    onClick: (event: React.SyntheticEvent) => {
                      event.stopPropagation();
                      handleDelete({ id: row.id, name: row.name });
                    },
                  },
                ]}
                variant="dots"
              />
            )}
            variant={TableVariant.List}
            onRowClick={(row) => {
              navigate(`${ROUTE.ENTITIES}/edit/${row.type?.toLowerCase() ?? 'person'}/${row.id}`);
            }}
          />
        </Inner>
      </Loading>
    </>
  );
};
